import React, { useEffect, useState } from 'react';
import { TBranch, TEditBranchDetailsReduxParams } from '~types/feat/branch';
import * as FormStyles from './_branchesDetailsForm.module.scss';
import ActionButton from '~components/atoms/ActionButton';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { useFormik } from 'formik';
import { BranchStatusEnum } from '~utils/enums';
import Yup from '~yup';
import LabelledInput from '~components/atoms/LabelledInput';
import Text from '~components/atoms/Text';
import { useAppSelector } from '~redux/hooks';
import { TDropdownOption } from '~types/components/labelledDropdown';

type TBranchesDetailsForm = {
    submitButtonLabel: string;
    onSubmit: (data: TEditBranchDetailsReduxParams) => void;
    error: string;
    isEditing?: boolean;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
    initialBranchDetails: TBranch;
}

const BranchesDetailsForm = (props: TBranchesDetailsForm) => {
    const {
        submitButtonLabel,
        error,
        isEditing,
        inputIsDisabled,
        initialBranchDetails,
        actionButtonIsLoading,
        actionButtonClassName,
        onSubmit,
    } = props;

    const [regionName, setRegionName] = useState('');

    const regions = useAppSelector(state => state.branch.allRegions);
    const branchDisabled = initialBranchDetails.status === BranchStatusEnum.Deactivated;

    const regionOptions = regions.map(region => {
        return {
            label: region.regionName,
            value: region.regionId,
        };
    });

    const statusOptions = Object.values(BranchStatusEnum).filter(e => Number.isNaN(Number(e))).filter(status => status !== BranchStatusEnum[BranchStatusEnum.Deleted]).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const getBranchDetailsSchema = () => Yup.object().shape({
        branchId: Yup.number(),
        branchCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersOnly().length(3).required('Branch Code is required'),
        branchName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersAndUppercaseAlphabetsOnly().required('Branch name is required'),
        regionId: Yup.number(),
        status: Yup.number().required('Status is required'),
    });

    const BranchInitialValues = {
        branchId: initialBranchDetails.branchId,
        branchCode: initialBranchDetails.branchCode,
        branchName: initialBranchDetails.branchName,
        regionId: initialBranchDetails.regionId,
        status: initialBranchDetails.status,
    };

    const formik = useFormik({
        initialValues: BranchInitialValues,
        validationSchema: getBranchDetailsSchema(),
        onSubmit: values => {
            onSubmit(values);
        },
    });

    useEffect(() => {
        getRegionName(initialBranchDetails.regionId);
    }, [initialBranchDetails]);

    useEffect(() => {
        if (regionName) {
            formik.setFieldValue('regionId', regions.find(region => region.regionName === regionName)?.regionId);
        } else formik.setFieldValue('regionId', '');
    }, [regionName]);

    const handleOnSearchRegion = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRegionName(event.target.value);
    };

    const handleOnSelectRegion = (option: TDropdownOption) => {
        const region = getRegionName(option.value);

        if (!region) return;

        formik.setValues({
            ...formik.values,
            regionId: region.regionId,
        });
    };

    const getRegionName = (selectedRegionId: string | number) => {
        const region = regions.find((regionOption) => regionOption.regionId === selectedRegionId);

        if (!region) return;

        setRegionName(region.regionName);

        return region;
    };

    return (
        <div className={FormStyles.container}>

            <form
                onSubmit={formik.handleSubmit}
                className={FormStyles.container}
            >
                <div className={FormStyles['form-container']}>
                    <LabelledInput
                        id='branchCode'
                        name='branchCode'
                        label='Branch Code :'
                        value={formik.values.branchCode}
                        error={formik.touched.branchCode ? formik.errors.branchCode : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={inputIsDisabled || branchDisabled}
                    />

                    <LabelledInput
                        id='branchName'
                        name='branchName'
                        label='Branch Name :'
                        value={formik.values.branchName}
                        error={formik.touched.branchName ? formik.errors.branchName : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={inputIsDisabled || branchDisabled}
                    />

                    <LabelledDropdown
                        id='regionId'
                        name='regionId'
                        label='Region* :'
                        options={regionOptions}
                        value={regionName}
                        error={formik.touched.regionId ? formik.errors.regionId || formik.errors.regionId : undefined}
                        onChange={handleOnSearchRegion}
                        onSelectOption={handleOnSelectRegion}
                        onBlur={formik.handleBlur}
                        autoComplete='off'
                        disabled={inputIsDisabled || branchDisabled}
                        noOptionsMessage='No region found'
                        optionSelected={!!regionOptions.find(option => option.value === formik.values.regionId)}
                    />

                    <LabelledDropdown
                        id='status'
                        name={'status'}
                        label='Status :'
                        options={statusOptions}
                        value={BranchStatusEnum[formik.values.status] || ''}
                        onChange={(e) => formik.setFieldValue('status', e.target.value === 'Active' ? BranchStatusEnum.Active : BranchStatusEnum.Deactivated)}
                        onBlur={formik.handleBlur}
                        autoComplete='off'
                        disabled={inputIsDisabled}
                        noOptionsMessage='No status found'
                        optionSelected={!!statusOptions.find(option => option.value === BranchStatusEnum[formik.values.status])}
                    />
                </div>

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={submitButtonLabel === 'Delete' ? false : !isEditing || !formik.dirty}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </form >
        </div>
    );
};

export default BranchesDetailsForm;
